import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RotateCcw, Check, X, Clock, Wallet, ShieldCheck, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const MONTH_LABELS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const fmtMonth = (m) => {
  const [, mm] = (m || "").split("-");
  const idx = parseInt(mm, 10) - 1;
  return MONTH_LABELS[idx] ?? m;
};

export default function AdminRefunds() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    api.get(`${V1}/admin/refund-requests`).then((r) => setItems(r.data || []));
    api.get(`${V1}/admin/refund-requests/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get(`${V1}/admin/refund-requests/trend`).then((r) => setTrend(r.data || [])).catch(() => setTrend([]));
  };
  useEffect(() => { load(); }, []);

  const decide = async (approve) => {
    try {
      const { data } = await api.post(`${V1}/admin/refund-requests/${decision.id}/decide`, { approve, admin_note: note });
      if (approve && data.iyzico_refunded) {
        toast.success("İyzico üzerinden gerçek iade işlendi ve onaylandı");
      } else if (approve) {
        toast.success("İade onaylandı (test/mağaza ödemesi — gerçek para hareketi yok)");
      } else {
        toast.success("Talep reddedildi");
      }
      setDecision(null); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-refunds-page">
      <div>
        <div className="overline text-accent">Ticaret</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><RotateCcw className="h-7 w-7 text-primary" /> İade Talepleri</h1>
        <p className="text-muted-foreground text-sm mt-1">Kullanıcı iade talepleri ve karar geçmişi.</p>
      </div>

      {stats && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3" data-testid="refund-stats-real">
            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center"><Wallet className="h-5 w-5 text-emerald-600" /></div>
            <div>
              <div className="text-xs text-muted-foreground">Bu Ay Gerçek İade (İyzico)</div>
              <div className="text-xl font-bold" data-testid="refund-stats-real-amount">₺{stats.real_refund_total} <span className="text-xs font-normal text-muted-foreground">· {stats.real_refund_count} işlem</span></div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3" data-testid="refund-stats-internal">
            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-slate-500" /></div>
            <div>
              <div className="text-xs text-muted-foreground">Test / Mağaza İadesi (para hareketi yok)</div>
              <div className="text-xl font-bold" data-testid="refund-stats-internal-amount">₺{stats.internal_refund_total} <span className="text-xs font-normal text-muted-foreground">· {stats.internal_refund_count} işlem</span></div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-5" data-testid="refund-trend-chart">
        <div className="font-display font-bold mb-1 flex items-center gap-2"><TrendingDown className="h-4 w-4 text-primary" /> Aylık İade Trendi</div>
        <p className="text-xs text-muted-foreground mb-3">Son 6 ayda onaylanan iade tutarı — gerçek (İyzico) ve test/mağaza ayrımıyla.</p>
        {trend.every((t) => t.real_total === 0 && t.internal_total === 0) ? (
          <div className="text-sm text-muted-foreground py-10 text-center">Bu dönemde onaylanmış iade yok</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend.map((t) => ({ ...t, label: fmtMonth(t.month) }))}>
              <XAxis dataKey="label" fontSize={12} />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip formatter={(v) => `₺${v}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="real_total" name="Gerçek İade (İyzico)" fill="#059669" radius={[4, 4, 0, 0]} data-testid="refund-trend-bar-real" />
              <Bar dataKey="internal_total" name="Test / Mağaza İadesi" fill="#94a3b8" radius={[4, 4, 0, 0]} data-testid="refund-trend-bar-internal" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="text-left p-3">Tarih</th><th className="text-left p-3">Ürün</th><th className="text-left p-3">Tutar</th><th className="text-left p-3">Sebep</th><th className="text-left p-3">Durum</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Talep yok</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`refund-${r.id}`}>
                <td className="p-3 text-xs">{new Date(r.created_at).toLocaleString("tr-TR")}</td>
                <td className="p-3">{r.kind} · {r.code}</td>
                <td className="p-3 font-bold">₺{r.amount}</td>
                <td className="p-3 text-xs max-w-xs truncate" title={r.reason}>{r.reason}</td>
                <td className="p-3">
                  {r.status === "pending" && <span className="inline-flex items-center gap-1 text-amber-600 text-xs"><Clock className="h-3 w-3" /> Bekliyor</span>}
                  {r.status === "approved" && <span className="inline-flex items-center gap-1 text-success text-xs"><Check className="h-3 w-3" /> Onaylandı</span>}
                  {r.status === "rejected" && <span className="inline-flex items-center gap-1 text-destructive text-xs"><X className="h-3 w-3" /> Reddedildi</span>}
                </td>
                <td className="p-3 text-right">
                  {r.status === "pending" && <Button size="sm" variant="outline" onClick={() => setDecision(r)} data-testid={`decide-${r.id}`}>Karar Ver</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>İade Kararı</DialogTitle>
            <DialogDescription>{decision?.kind} · {decision?.code} · ₺{decision?.amount}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="p-3 rounded bg-muted/30 text-sm">
              <div className="text-xs text-muted-foreground mb-1">Talep sebebi</div>
              {decision?.reason}
            </div>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Admin notu (kullanıcıya iletilir)" data-testid="refund-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => decide(false)} data-testid="reject-refund-btn"><X className="h-4 w-4" /> Reddet</Button>
            <Button onClick={() => decide(true)} data-testid="approve-refund-btn"><Check className="h-4 w-4" /> Onayla & İade Et</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
